/**
 * useCanvasIdleIntervention — Detects when a user lingers on a canvas section
 * and proactively offers contextual help from the agent.
 *
 * Triggers after 5 seconds of idle hover/focus on a resume section.
 * Only fires once per section per session to avoid annoyance.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface IdleInterventionResult {
  /** Section the user is lingering on (null = no active intervention). */
  lingeringSection: string | null;
  /** Dismiss the current intervention. */
  dismiss: () => void;
  /** Message text to show as a nudge. */
  nudgeMessage: string;
  /** Mark a section as having received its nudge (won't trigger again). */
  onSectionInteraction: (sectionTitle: string) => void;
}

const IDLE_TIMEOUT_MS = 5000;

const SECTION_NUDGES: Record<string, string> = {
  summary: 'Need help making your summary more impactful? I can add power words and align it to the job description.',
  experience: 'Want me to strengthen these bullet points with action verbs and quantifiable metrics?',
  skills: 'I can reorganize your skills to match what ATS systems look for — prioritizing keywords from the job description.',
  education: 'Should I format your education section for maximum ATS compatibility?',
  projects: 'I can help highlight the business impact and technologies used in your projects.',
  certifications: 'Want me to prioritize your certifications by relevance to this role?',
};

const DEFAULT_NUDGE = 'Need help with this section? Just tell me what you\'d like to change.';

function getSectionKey(title: string): string {
  const lower = title.toLowerCase();
  if (lower.includes('summary') || lower.includes('objective') || lower.includes('profile')) return 'summary';
  if (lower.includes('experience') || lower.includes('employment') || lower.includes('work')) return 'experience';
  if (lower.includes('skill') || lower.includes('technical') || lower.includes('competenc')) return 'skills';
  if (lower.includes('education') || lower.includes('academic')) return 'education';
  if (lower.includes('project')) return 'projects';
  if (lower.includes('certif') || lower.includes('license')) return 'certifications';
  return '';
}

export function useCanvasIdleIntervention(): IdleInterventionResult {
  const [lingeringSection, setLingeringSection] = useState<string | null>(null);
  const [nudgeMessage, setNudgeMessage] = useState('');
  const nudgedSections = useRef(new Set<string>());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSectionRef = useRef<string | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    setLingeringSection(null);
    setNudgeMessage('');
    clearTimer();
  }, [clearTimer]);

  const onSectionInteraction = useCallback((sectionTitle: string) => {
    const key = getSectionKey(sectionTitle);
    const sectionId = key || sectionTitle;

    if (nudgedSections.current.has(sectionId)) {
      clearTimer();
      return;
    }

    if (activeSectionRef.current !== sectionTitle) {
      clearTimer();
      dismiss();
      activeSectionRef.current = sectionTitle;
    }

    clearTimer();
    timerRef.current = setTimeout(() => {
      nudgedSections.current.add(sectionId);
      const message = SECTION_NUDGES[key] || DEFAULT_NUDGE;
      setLingeringSection(sectionTitle);
      setNudgeMessage(message);
    }, IDLE_TIMEOUT_MS);
  }, [clearTimer, dismiss]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return { lingeringSection, dismiss, nudgeMessage, onSectionInteraction };
}
